import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Globe, Gem, Coins, Flame, Sprout, Box, Plane } from 'lucide-react';
import { commoditiesCategories } from '../data/mock';

export const InternationalTrade = () => {
  const iconMap = {
    Gem,
    Coins,
    Flame,
    Sprout,
    Box,
    Plane
  };

  return (
    <div className="service-detail-page">
      {/* Breadcrumb */}
      <div className="breadcrumb-container">
        <Link to="/" className="breadcrumb-link">
          <ArrowLeft size={18} />
          Back to Home
        </Link>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">International Trade</span>
      </div>

      {/* Hero Section */}
      <section className="service-detail-hero">
        <div className="service-detail-icon">
          <Globe size={48} />
        </div>
        <h1 className="service-detail-title">International Trade</h1>
        <p className="service-detail-subtitle">
          Global trade facilitation and transaction advisory across commodities, energy, and specialized assets
        </p>
      </section>

      {/* Overview */}
      <section className="service-detail-section">
        <div className="service-detail-content">
          <h2 className="service-section-title">What We Do</h2>
          <p className="service-section-text">
            We ensure efficient transactions in competitive international markets with credible counterparties. Our trade advisory services span commodities, energy products, industrial materials, and specialized assets, connecting buyers and sellers across global markets with comprehensive due diligence and transaction support.
          </p>
          
          {/* Pipeline Metric */}
          <div className="service-pipeline-highlight" style={{marginTop: '2rem'}}>
            <h3 className="pipeline-title">Global Trade Pipeline</h3>
            <div className="pipeline-metric-large">USD 50 Mn+</div>
            <p className="pipeline-description-large">
              Ongoing trade advisory mandates across commodities, energy products, and specialized assets.
            </p>
          </div>
        </div>
      </section>

      {/* Services Offered */}
      <section className="service-detail-section alt-bg">
        <div className="service-detail-content">
          <h2 className="service-section-title">Our Services</h2>
          <div className="service-features-grid">
            <div className="service-feature-card">
              <CheckCircle size={24} className="feature-icon" />
              <h3 className="feature-card-title">Precious Metals & Gems</h3>
              <p className="feature-card-text">
                Trading and transaction facilitation for precious metals, gemstones, and related commodities with verified counterparties
              </p>
            </div>
            <div className="service-feature-card">
              <CheckCircle size={24} className="feature-icon" />
              <h3 className="feature-card-title">Energy Products</h3>
              <p className="feature-card-text">
                Advisory for energy and petrochemical products trading including crude oil, refined products, and petrochemicals
              </p>
            </div>
            <div className="service-feature-card">
              <CheckCircle size={24} className="feature-icon" />
              <h3 className="feature-card-title">Agricultural Commodities</h3>
              <p className="feature-card-text">
                Trade facilitation for agricultural and food commodities with quality assurance and logistics coordination
              </p>
            </div>
            <div className="service-feature-card">
              <CheckCircle size={24} className="feature-icon" />
              <h3 className="feature-card-title">Industrial Materials</h3>
              <p className="feature-card-text">
                Trading advisory for industrial materials, metals, and manufactured products across international markets
              </p>
            </div>
            <div className="service-feature-card">
              <CheckCircle size={24} className="feature-icon" />
              <h3 className="feature-card-title">Aviation Assets</h3>
              <p className="feature-card-text">
                Specialized advisory for aviation assets and related equipment with technical due diligence support
              </p>
            </div>
            <div className="service-feature-card">
              <CheckCircle size={24} className="feature-icon" />
              <h3 className="feature-card-title">Trade Finance</h3>
              <p className="feature-card-text">
                Coordination of trade finance instruments including letters of credit, bank guarantees, and payment terms
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Markets */}
      <section className="service-detail-section">
        <div className="service-detail-content">
          <h2 className="service-section-title">Key Markets & Regions</h2>
          <div className="markets-grid">
            <div className="market-card">
              <h3 className="market-title">Asia Pacific</h3>
              <p className="market-text">India, China, Southeast Asia, Australia</p>
            </div>
            <div className="market-card">
              <h3 className="market-title">Middle East</h3>
              <p className="market-text">UAE, Saudi Arabia, Qatar, Kuwait</p>
            </div>
            <div className="market-card">
              <h3 className="market-title">Europe</h3>
              <p className="market-text">UK, Netherlands, Germany, Switzerland</p>
            </div>
            <div className="market-card">
              <h3 className="market-title">Americas</h3>
              <p className="market-text">USA, Canada, Brazil, Mexico</p>
            </div>
          </div>
        </div>
      </section>

      {/* Commodity Categories - Detailed */}
      <section className="service-detail-section alt-bg">
        <div className="service-detail-content">
          <h2 className="service-section-title">Commodities We Deal In</h2>
          <p className="service-section-text" style={{marginBottom: '2rem'}}>
            Professional advisory services for institutional buyers, sellers, and trading partners across key commodity sectors.
          </p>
          <div className="service-features-grid">
            {commoditiesCategories.map((category) => {
              const IconComponent = iconMap[category.icon];
              return (
                <div key={category.id} className="service-feature-card">
                  <div className="feature-icon" style={{color: '#1e3a8a'}}>
                    {IconComponent && <IconComponent size={28} />}
                  </div>
                  <h3 className="feature-card-title">{category.category}</h3>
                  <ul className="feature-card-list">
                    {category.items.map((item, idx) => (
                      <li key={idx} className="feature-card-list-item">• {item}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="service-detail-section">
        <div className="service-detail-content">
          <div className="service-cta-box">
            <h2 className="cta-title">Explore International Trade Opportunities</h2>
            <p className="cta-text">
              Connect with our trade advisory team to discuss commodity transactions, counterparty verification, or cross-border trade facilitation.
            </p>
            <Link to="/#contact" className="btn-primary">
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
